/** Posting wizard + activity/mechanic/unit matrix. Template metadata comes from GET …/community_challenges/templates (TemplateRegistry). */

export const COMMUNITY_CHALLENGE_MODES = {
	CUSTOM: 'custom',
	FEATURED: 'featured'
};

/** How someone wins. */
export const COMMUNITY_MECHANICS = [
	{
		id: 'most',
		label: 'Most',
		hint: 'Highest total or best count wins'
	},
	{
		id: 'fastest',
		label: 'Fastest time',
		hint: 'Lowest time wins — submit your best at the end'
	},
	{
		id: 'streak',
		label: 'Longest streak',
		hint: 'Most consecutive days or attempts'
	},
	{
		id: 'first_to',
		label: 'First to',
		hint: 'First person to hit a target (coming soon)',
		comingSoon: true
	},
	{
		id: 'reach',
		label: 'Reach an amount',
		hint: 'Everyone races to a goal (coming soon)',
		comingSoon: true
	},
	{
		id: 'quickest',
		label: 'Quickest to',
		hint: 'Fastest to complete a set task (coming soon)',
		comingSoon: true
	}
];

export const MEASUREMENT_UNITS = [
	{ id: 'count', label: 'Count' },
	{ id: 'miles', label: 'Miles' },
	{ id: 'laps', label: 'Laps' },
	{ id: 'days', label: 'Days' },
	{ id: 'minutes', label: 'Minutes' },
	{ id: 'seconds', label: 'Seconds' },
	{ id: 'other', label: 'Other', isCustom: true }
];

const MECHANIC_UNIT_IDS = {
	most: ['count', 'miles', 'laps', 'days', 'minutes', 'seconds', 'other'],
	fastest: ['days', 'minutes', 'seconds', 'miles', 'laps', 'other'],
	streak: ['days', 'count', 'other']
};

export const COMMUNITY_DURATION_PRESETS_STANDARD = [
	{ days: 3, label: '3 days' },
	{ days: 7, label: '1 week' },
	{ days: 30, label: '1 month' }
];

/**
 * What participants are doing.
 * @typedef {{ id: string, label: string, subjectLabel?: string, isCustom?: boolean, mechanics?: string[] }} ChallengeActivity
 */
export const CHALLENGE_ACTIVITIES = [
	{
		id: 'running',
		label: 'Running',
		subjectLabel: 'running',
		mechanics: ['most', 'fastest', 'streak']
	},
	{
		id: 'walking',
		label: 'Walking',
		subjectLabel: 'walking',
		mechanics: ['most', 'streak']
	},
	{
		id: 'swimming',
		label: 'Swimming',
		subjectLabel: 'swimming',
		mechanics: ['most', 'fastest', 'streak']
	},
	{
		id: 'biking',
		label: 'Biking',
		subjectLabel: 'biking',
		mechanics: ['most', 'fastest', 'streak']
	},
	{
		id: 'hiking',
		label: 'Hiking',
		subjectLabel: 'hiking',
		mechanics: ['most', 'streak']
	},
	{
		id: 'rowing',
		label: 'Rowing',
		subjectLabel: 'rowing',
		mechanics: ['most', 'fastest', 'streak']
	},
	{
		id: 'situps',
		label: 'Sit-ups',
		mechanics: ['most']
	},
	{
		id: 'pushups',
		label: 'Push-ups',
		subjectLabel: 'push-ups',
		mechanics: ['most']
	},
	{
		id: 'pullups',
		label: 'Pull-ups',
		subjectLabel: 'pull-ups',
		mechanics: ['most']
	},
	{
		id: 'jumping_jacks',
		label: 'Jumping jacks',
		mechanics: ['most']
	},
	{
		id: 'planks',
		label: 'Planks',
		subjectLabel: 'planks',
		mechanics: ['most']
	},
	{
		id: 'burpees',
		label: 'Burpees',
		subjectLabel: 'burpees',
		mechanics: ['most']
	},
	{
		id: 'squats',
		label: 'Squats',
		subjectLabel: 'squats',
		mechanics: ['most']
	},
	{
		id: 'lunges',
		label: 'Lunges',
		subjectLabel: 'lunges',
		mechanics: ['most']
	},
	{
		id: 'free_throws',
		label: 'Free throws',
		mechanics: ['most', 'streak']
	},
	{
		id: 'yoga',
		label: 'Yoga',
		subjectLabel: 'yoga sessions',
		mechanics: ['most', 'streak']
	},
	{
		id: 'workouts',
		label: 'Workouts',
		subjectLabel: 'workouts',
		mechanics: ['most', 'streak']
	},
	{
		id: 'other',
		label: 'Other',
		isCustom: true,
		mechanics: ['most', 'fastest', 'streak']
	}
];

/** Fallback when catalog has not loaded yet. */
export const MOST_CUSTOM_TEMPLATE_KEY = 'most_custom';

export function findCommunityTemplate(key, catalog = []) {
	return catalog.find((t) => t.key === key) || catalog.find((t) => t.key === MOST_CUSTOM_TEMPLATE_KEY) || null;
}

export function featuredCommunityTemplates(catalog = []) {
	return catalog.filter((t) => t.flow === 'featured');
}

export function resolveCommunityTemplateKey({
	mode,
	mechanic,
	activityId,
	unitId,
	featuredKey,
	catalog = [],
	wizard = null
}) {
	if (mode === COMMUNITY_CHALLENGE_MODES.FEATURED) {
		const featured = featuredCommunityTemplates(catalog).find((t) => t.key === featuredKey);
		const fallback = featuredCommunityTemplates(catalog)[0];
		return featured?.key || fallback?.key || featuredKey;
	}

	if (!wizard) return MOST_CUSTOM_TEMPLATE_KEY;

	const override = (wizard.activity_overrides || []).find(
		(rule) => rule.activity === activityId && rule.mechanic === mechanic && rule.unit === unitId
	);
	if (override?.template_key) return override.template_key;

	const defaults = wizard.mechanic_unit_defaults?.[mechanic] || {};
	if (unitId && defaults[unitId]) return defaults[unitId];

	return defaults.default || wizard.fallback_template_key || MOST_CUSTOM_TEMPLATE_KEY;
}

export function buildActivityCustomParams({
	activityId,
	customActivityText,
	mechanic,
	unitId,
	customUnitText,
	templateKey
}) {
	const activity = findChallengeActivity(activityId);
	const params = {
		activity: activityId,
		mechanic,
		measurement_unit: unitId
	};

	if (unitId === 'other' && customUnitText?.trim()) {
		params.unit_label = customUnitText.trim();
	}

	if (activity?.isCustom && customActivityText?.trim()) {
		params.subject_label = customActivityText.trim();
	} else if (templateKey === 'most_custom' && activity?.subjectLabel) {
		params.subject_label = activity.subjectLabel;
	}

	return params;
}

export function findChallengeActivity(activityId) {
	if (!activityId) return null;
	return CHALLENGE_ACTIVITIES.find((a) => a.id === activityId) || null;
}

export function findMeasurementUnit(unitId) {
	if (!unitId) return null;
	return MEASUREMENT_UNITS.find((u) => u.id === unitId) || null;
}

export function mechanicsForActivity(activityId) {
	if (!activityId) return [];
	const activity = findChallengeActivity(activityId);
	if (!activity) return [];
	const allowed = activity.mechanics || ['most'];
	return COMMUNITY_MECHANICS.filter((mechanic) => {
		if (mechanic.comingSoon) return false;
		return allowed.includes(mechanic.id);
	});
}

export function unitsForMechanic(mechanicId) {
	if (!mechanicId) return [];
	const allowed = MECHANIC_UNIT_IDS[mechanicId] || MECHANIC_UNIT_IDS.most;
	return MEASUREMENT_UNITS.filter((unit) => allowed.includes(unit.id));
}

export function defaultUnitForMechanic(mechanicId) {
	const units = unitsForMechanic(mechanicId);
	if (mechanicId === 'fastest') {
		return units.find((u) => u.id === 'minutes')?.id || units[0]?.id || 'count';
	}
	if (mechanicId === 'streak') {
		return units.find((u) => u.id === 'days')?.id || units[0]?.id || 'count';
	}
	return units[0]?.id || 'count';
}

export function activityNeedsCustomText(activityId) {
	return findChallengeActivity(activityId)?.isCustom === true;
}

export function unitNeedsCustomText(unitId) {
	return findMeasurementUnit(unitId)?.isCustom === true;
}

export function defaultCustomSelection() {
	return { activityId: '', mechanic: '', unitId: '' };
}
